'use client';

import { forwardRef, type ReactNode } from 'react';

/**
 * Shared styling and small building blocks for the waitlist forms and the
 * emailed-link pages, so the join form, the manage view and the request forms
 * look and behave the same. Native controls only; 48px inputs, 44px+ targets.
 */

export const inputClass =
  'min-h-12 w-full rounded-lg border border-grey-300 bg-white px-4 py-3 text-base text-grey-900 outline-none transition-colors hover:border-grey-400 focus-visible:border-brand-600 focus-visible:ring-3 focus-visible:ring-brand-600/25 aria-[invalid=true]:border-error-600 aria-[invalid=true]:focus-visible:ring-error-600/25 disabled:bg-grey-50 disabled:text-grey-600';

export const labelClass = 'text-sm font-medium text-grey-800';
export const hintClass = 'text-sm text-grey-600';
export const errorClass = 'text-sm text-error-700';

const buttonBase =
  'inline-flex min-h-12 items-center justify-center rounded-lg px-5 text-base font-semibold transition-colors focus-ring disabled:cursor-not-allowed disabled:opacity-70';
export const primaryButtonClass = `${buttonBase} bg-brand-600 text-white hover:bg-brand-700`;
export const secondaryButtonClass = `${buttonBase} bg-brand-25 text-brand-800 hover:bg-brand-50`;
export const dangerButtonClass = `${buttonBase} bg-error-700 text-white hover:bg-error-800`;
export const dangerQuietButtonClass = `${buttonBase} bg-white text-error-700 ring-1 ring-inset ring-error-300 hover:bg-error-50`;

export const cardClass = 'rounded-2xl border border-grey-200 bg-white p-6 text-grey-800 sm:p-8';

type Tone = 'info' | 'success' | 'error' | 'warning';

const toneClass: Record<Tone, string> = {
  info: 'bg-brand-25 text-brand-900',
  success: 'bg-success-50 text-success-800',
  error: 'bg-error-50 text-error-700',
  warning: 'bg-warning-50 text-warning-800',
};

/**
 * A live region that is always in the DOM (so screen readers register it
 * before it changes) and is styled only when it has something to say. Errors
 * are `alert`; everything else is a polite `status`.
 */
export const LiveMessage = forwardRef<HTMLDivElement, { tone: Tone; children?: ReactNode; className?: string }>(
  function LiveMessage({ tone, children, className = '' }, ref) {
    const has = children !== undefined && children !== null && children !== '' && children !== false;
    return (
      <div
        ref={ref}
        tabIndex={-1}
        role={tone === 'error' ? 'alert' : 'status'}
        className={`text-[15px] leading-relaxed outline-none ${has ? `rounded-lg px-4 py-3 ${toneClass[tone]}` : ''} ${className}`}
      >
        {has ? children : null}
      </div>
    );
  },
);

export interface SummaryItem {
  id: string;
  message: string;
}

/**
 * The error summary shown above a form after a failed submit. It takes focus
 * so the problem is announced, and each item moves focus to its field.
 */
export const ErrorSummary = forwardRef<HTMLDivElement, { items: SummaryItem[]; titleId: string }>(function ErrorSummary(
  { items, titleId },
  ref,
) {
  if (!items.length) return null;
  return (
    <div ref={ref} tabIndex={-1} aria-labelledby={titleId} className="rounded-lg border border-error-300 bg-error-50 px-4 py-3 outline-none focus-visible:ring-3 focus-visible:ring-error-600/25">
      <h3 id={titleId} className="font-semibold text-error-800">
        {items.length === 1 ? 'There is a problem' : `There are ${items.length} problems`}
      </h3>
      <ul className="mt-1.5 list-disc pl-5 text-[15px] text-error-700">
        {items.map((i) => (
          <li key={i.id}>
            <a
              href={`#${i.id}`}
              className="underline underline-offset-2 focus-ring rounded-sm"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(i.id)?.focus();
              }}
            >
              {i.message}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
});

/** The honeypot: off-screen and out of the tab order; people never fill it. */
export function Honeypot({ id, value, onChange }: { id: string; value: string; onChange: (v: string) => void }) {
  return (
    <div aria-hidden="true" className="absolute -left-[10000px] h-px w-px overflow-hidden">
      <label htmlFor={id}>Leave this field empty</label>
      <input id={id} name="website" type="text" tabIndex={-1} autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

/** Seconds to a readable wait, rounded up to whole minutes past one. */
export function waitCopy(seconds: number | undefined): string {
  if (!seconds || seconds <= 60) return 'a minute';
  const m = Math.ceil(seconds / 60);
  return `${m} minutes`;
}
